/**
 * CommunityFeed.jsx — PRODUCTION v5.0 "Responsive Edition"
 * Place at: src/pages/CommunityFeed.jsx
 *
 * FULLY RESPONSIVE — looks world-class on both mobile and desktop.
 *
 * MOBILE CHANGES vs v4:
 *   • Single-column layout (no sidebar)
 *   • Sidebar content moved into collapsible bottom sheet ("More" button)
 *   • Navbar hidden on mobile (MobileShell provides its own nav)
 *   • Touch-friendly tap targets, no hover-only interactions
 *   • iOS inset search bar style
 *   • Spaces accessible via a prominent button
 *   • Trending tags in a horizontal scroll strip (not a sidebar card)
 *   • User stats shown in a mini bar below the composer
 *   • All desktop functionality preserved
 *
 * BACKEND 100% PRESERVED:
 *   • All RPCs: get_feed_with_user_state, get_following_posts, get_bookmarked_posts
 *   • FeedContext with 5-min cache, optimistic updates, AbortController
 *   • Pagination, sort, tag filter, search, follow, vote, bookmark, report
 */

import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { auth } from "../firebase";
import { signOut } from "firebase/auth";
import { supabase } from "../supabase";
import { MdOutlineLightMode, MdModeNight, MdFilterVintage } from "react-icons/md";
import { FaUserCircle } from "react-icons/fa";

import { useFeedUser }         from "../hooks/useFeedUser";
import { useTopSpaces }        from "../hooks/useTopSpaces";
import { FeedProvider }        from "../context/FeedContext";

import UsernameSetup           from "../components/UsernameSetup";
import PostComposer            from "../components/PostComposer";
import PostCard                from "../components/PostCard";
import NotificationBell        from "../components/NotificationBell";
import UserProfilePanel        from "../components/UserProfilePanel";
import AdminPanel              from "../components/AdminPanel";
import SpaceCard               from "../components/SpaceCard";
import SpaceFeed               from "./SpaceFeed";
import CreateSpaceModal        from "../components/CreateSpaceModal";
import ErrorBoundary           from "../components/ErrorBoundary";

import { TAGS, SORT_OPTIONS }  from "../utils/moderation";
import { changeUsername }       from "../lib/spaceQueries";
import { searchSpaces }         from "../lib/spaceQueries";

const POSTS_PER_PAGE = 10;

/* ─── Responsive hook ────────────────────────────────────────────────────── */
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );
  useEffect(() => {
    const fn = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);
  return isMobile;
}

/* ─── Responsive CSS injected once ──────────────────────────────────────── */
const RESPONSIVE_CSS = `
/* Hide desktop navbar on mobile — mobile shell provides its own */
@media (max-width: 767px) {
  .cf-navbar { display: none !important; }
  .cf-desktop-sidebar { display: none !important; }
}
@media (min-width: 768px) {
  .cf-mobile-only { display: none !important; }
  .cf-mobile-bottom-sheet-overlay { display: none !important; }
}
@keyframes cfSheetUp   { from{transform:translateY(100%)} to{transform:translateY(0)} }
@keyframes cfFadeIn    { from{opacity:0} to{opacity:1} }
@keyframes cfBounceIn  { from{opacity:0;transform:scale(.9) translateY(8px)} to{opacity:1;transform:none} }
`;

let cfCssInjected = false;
function injectCFCSS() {
  if (cfCssInjected || typeof document === "undefined") return;
  cfCssInjected = true;
  const el = document.createElement("style");
  el.textContent = RESPONSIVE_CSS;
  document.head.appendChild(el);
}

/* ═══════════════════════════════════════════════════════════════════════════
   MOBILE MORE SHEET — shows sidebar content on mobile
═══════════════════════════════════════════════════════════════════════════ */
function MobileMoreSheet({ feedUser, trendingTags, topSpaces, activeTag, setActiveTag, onClose, onOpenSpace, onCreateSpace, canCreateSpace, onOpenSpaceBrowser, spaceSearch, setSpaceSearch, spaceResults, searchingSpaces }) {
  return (
    <div
      className="cf-mobile-bottom-sheet-overlay"
      style={{
        position: "fixed", inset: 0, zIndex: 300,
        background: "rgba(0,0,0,.5)",
        backdropFilter: "blur(5px)", WebkitBackdropFilter: "blur(5px)",
        animation: "cfFadeIn .18s ease",
      }}
      onClick={onClose}
    >
      <div
        style={{
          position: "fixed", bottom: 0, left: 0, right: 0,
          background: "var(--bg-secondary, #F9F4F0)",
          borderRadius: "22px 22px 0 0",
          maxHeight: "82dvh",
          display: "flex", flexDirection: "column",
          boxShadow: "0 -8px 32px rgba(0,0,0,.12)",
          animation: "cfSheetUp .28s cubic-bezier(.25,1,.4,1)",
          overflow: "hidden",
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Handle */}
        <div style={{
          width: 36, height: 4, borderRadius: 99,
          background: "var(--border-color, #D6D2C4)",
          margin: "12px auto 4px", flexShrink: 0,
        }} />

        {/* Title */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "10px 18px 12px",
          borderBottom: "1px solid var(--border-color)",
          flexShrink: 0,
        }}>
          <span style={{
            fontFamily: "var(--font-display, serif)",
            fontSize: "1.05rem", fontWeight: 700,
            color: "var(--text-color)",
          }}>Discover</span>
          <button onClick={onClose} style={{
            width: 28, height: 28, borderRadius: "50%",
            background: "rgba(118,118,128,.13)", border: "none",
            color: "var(--text-color)", fontWeight: 700, fontSize: ".85rem",
            display: "flex", alignItems: "center", justifyContent: "center",
            cursor: "pointer",
          }}>✕</button>
        </div>

        {/* Scrollable content */}
        <div style={{
          flex: 1, overflowY: "auto",
          padding: "14px 16px calc(env(safe-area-inset-bottom,0px) + 16px)",
          scrollbarWidth: "none",
        }}>
          {/* Spaces section */}
          <div style={{ marginBottom: 20 }}>
            <div style={{
              display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10,
            }}>
              <span style={{
                fontSize: ".65rem", fontWeight: 800,
                color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: ".07em",
              }}>🏡 Spaces</span>
              {feedUser && (feedUser.follower_count >= 50 || feedUser.is_admin) && (
                <button
                  onClick={() => { onCreateSpace(); onClose(); }}
                  style={{
                    padding: "4px 12px", borderRadius: 99,
                    border: "1.5px solid var(--primary-color)",
                    background: "var(--primary-light)",
                    color: "var(--primary-color)",
                    fontSize: ".72rem", fontWeight: 700, cursor: "pointer",
                    fontFamily: "inherit",
                  }}
                >+ Create</button>
              )}
            </div>

            {/* Search spaces */}
            <div style={{
              display: "flex", alignItems: "center", gap: 7,
              background: "rgba(118,118,128,.1)", borderRadius: 11, padding: "8px 12px",
              marginBottom: 10,
            }}>
              <span style={{ fontSize: 13, opacity: .5 }}>🔍</span>
              <input
                value={spaceSearch}
                onChange={e => setSpaceSearch(e.target.value)}
                placeholder="Search spaces…"
                style={{
                  flex: 1, border: "none", background: "transparent", padding: 0,
                  color: "var(--text-color)", fontFamily: "inherit",
                  fontSize: ".84rem", fontWeight: 500, outline: "none",
                }}
              />
            </div>

            {searchingSpaces ? (
              <div style={{ textAlign: "center", color: "var(--text-muted)", padding: "12px 0", fontSize: ".82rem" }}>
                Searching…
              </div>
            ) : spaceResults.length === 0 ? (
              <div style={{ textAlign: "center", color: "var(--text-muted)", fontSize: ".8rem", padding: "8px 0" }}>
                {spaceSearch ? "No spaces found" : "No spaces yet"}
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {spaceResults.map(space => (
                  <button
                    key={space.id}
                    onClick={() => { onOpenSpace(space); onClose(); }}
                    style={{
                      display: "flex", alignItems: "center", gap: 10, padding: "10px 12px",
                      borderRadius: 12, border: "1.5px solid var(--border-color)",
                      background: "var(--card-bg-color, #fff)", cursor: "pointer",
                      textAlign: "left", fontFamily: "inherit", width: "100%",
                    }}
                  >
                    <span style={{ fontSize: 20, flexShrink: 0 }}>{space.avatar_emoji || "🏡"}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: ".84rem", fontWeight: 700, color: "var(--text-color)",
                        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                      }}>{space.name}</div>
                      <div style={{ fontSize: ".7rem", color: "var(--text-muted)" }}>
                        {space.member_count} members
                        {space.user_role && space.user_role !== "none" && (
                          <span style={{ marginLeft: 4, color: space.color || "var(--primary-color)", fontWeight: 700 }}>
                            · {space.user_role === "moderator" ? "🛡️ Mod" : "✓ Joined"}
                          </span>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Trending tags */}
          {trendingTags.length > 0 && (
            <div>
              <div style={{
                fontSize: ".65rem", fontWeight: 800,
                color: "var(--text-muted)", textTransform: "uppercase",
                letterSpacing: ".07em", marginBottom: 10,
              }}>🔥 Trending This Week</div>
              {trendingTags.map(([tag, count]) => {
                const tagInfo = TAGS.find(t => t.label === tag) || {};
                return (
                  <button
                    key={tag}
                    onClick={() => { setActiveTag(tag === activeTag ? null : tag); onClose(); }}
                    style={{
                      display: "flex", justifyContent: "space-between", alignItems: "center",
                      width: "100%", padding: "10px 0", background: "none", border: "none",
                      borderBottom: "1px solid var(--border-color)",
                      cursor: "pointer", color: "var(--text-color)", fontFamily: "inherit",
                    }}
                  >
                    <span style={{ fontSize: ".88rem", fontWeight: 600 }}>{tagInfo.emoji} {tag}</span>
                    <span style={{ fontSize: ".78rem", color: "var(--text-muted)" }}>{count} posts</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Guidelines */}
          <div style={{
            marginTop: 20, padding: "14px 16px",
            borderRadius: 14,
            background: "linear-gradient(135deg, var(--primary-light), var(--card-bg-color))",
            border: "1px solid var(--border-color)",
          }}>
            <div style={{ fontWeight: 700, fontSize: ".85rem", marginBottom: 6 }}>💚 Community Guidelines</div>
            <p style={{ fontSize: ".78rem", margin: 0, lineHeight: 1.7, color: "var(--text-muted)" }}>
              Be kind and supportive. No hate, harassment, or explicit content.
              Use the report button for violations.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   INNER COMPONENT
═══════════════════════════════════════════════════════════════════════════ */
function CommunityFeedInner({ feedUser, userLoading, needsUsername, registerUsername, updateFeedUser }) {
  injectCFCSS();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { topSpaces } = useTopSpaces();

  /* Feed state */
  const [posts,        setPosts]        = useState([]);
  const [voteMap,      setVoteMap]      = useState({});
  const [bookmarkMap,  setBookmarkMap]  = useState({});
  const [reportMap,    setReportMap]    = useState({});
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [hasMore,      setHasMore]      = useState(true);
  const [page,         setPage]         = useState(0);

  /* Filters */
  const [feedTab,     setFeedTab]     = useState("all");
  const [sortBy,      setSortBy]      = useState("hot");
  const [activeTag,   setActiveTag]   = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchInput, setSearchInput] = useState("");

  /* UI state */
  const [theme,              setTheme]              = useState(localStorage.getItem("theme") || "light-theme");
  const [showInsightMenu,    setShowInsightMenu]    = useState(false);
  const [viewingProfile,     setViewingProfile]     = useState(null);
  const [showAdmin,          setShowAdmin]          = useState(false);
  const [trendingTags,       setTrendingTags]       = useState([]);
  const [openedSpace,        setOpenedSpace]        = useState(null);
  const [showCreateSpace,    setShowCreateSpace]    = useState(false);
  const [showUsernameChange, setShowUsernameChange] = useState(false);
  const [newUsername,        setNewUsername]        = useState("");
  const [usernameError,      setUsernameError]      = useState(null);
  const [usernameLoading,    setUsernameLoading]    = useState(false);
  const [spaceSearch,        setSpaceSearch]        = useState("");
  const [spaceSearchInput,   setSpaceSearchInput]   = useState("");
  const [spaceResults,       setSpaceResults]       = useState([]);
  const [searchingSpaces,    setSearchingSpaces]    = useState(false);
  const [showMobileMore,     setShowMobileMore]     = useState(false); // mobile "More" sheet

  const canCreateSpace = feedUser?.is_admin || (feedUser?.follower_count ?? 0) >= 50;

  /* Posts + space cards weaved in */
  const getPostsWithSpaceCards = useCallback(() => {
    if (!topSpaces.length || !posts.length) return posts;
    const result = [...posts];
    const cards  = topSpaces.slice(0, 2);
    const positions = [2, 5].filter(i => i < result.length);
    positions.forEach((pos, i) => {
      if (cards[i]) result.splice(pos + i, 0, { __isSpaceCard: true, space: cards[i] });
    });
    return result;
  }, [posts, topSpaces]);

  useEffect(() => { document.body.className = theme; }, [theme]);

  const toggleTheme = () => {
    const themes   = ["light-theme", "dark-theme", "zen-theme"];
    const newTheme = themes[(themes.indexOf(theme) + 1) % themes.length];
    setTheme(newTheme);
    localStorage.setItem("theme", newTheme);
    document.body.className = newTheme;
  };

  const handleLogout = async () => { await signOut(auth); navigate("/"); };

  const extractMaps = (rows) => {
    const voteM = {}, bookmarkM = {}, reportM = {};
    rows.forEach(p => {
      if (p.my_vote)       voteM[p.id]     = p.my_vote;
      if (p.is_bookmarked) bookmarkM[p.id] = true;
      if (p.is_reported)   reportM[p.id]   = true;
    });
    return { voteM, bookmarkM, reportM };
  };

  const fetchPosts = useCallback(async (reset = false) => {
    if (!feedUser) return;
    setLoadingPosts(true);
    const currentPage = reset ? 0 : page;
    const from = currentPage * POSTS_PER_PAGE;
    const to   = from + POSTS_PER_PAGE - 1;
    try {
      let rows = [];
      if (feedTab === "following") {
        const { data, error } = await supabase.rpc("get_following_posts", {
          p_user_id: feedUser.id, p_from: from, p_to: to,
        });
        if (error) throw error;
        rows = (data || []).filter(p => !p.is_deleted && !p.is_hidden);
      } else if (feedTab === "bookmarks") {
        const { data, error } = await supabase.rpc("get_bookmarked_posts", {
          p_user_id: feedUser.id, p_from: from, p_to: to,
        });
        if (error) throw error;
        rows = (data || []).filter(p => p && !p.is_deleted && !p.is_hidden);
      } else {
        const { data, error } = await supabase.rpc("get_feed_with_user_state", {
          p_user_id:   feedUser.id, p_from: from, p_to: to,
          p_tag:       activeTag    || null,
          p_search:    searchQuery  || null,
          p_sort:      sortBy,
          p_author_id: feedTab === "mine" ? feedUser.id : null,
        });
        if (error) throw error;
        rows = (data || []).filter(p => !p.is_deleted && !p.is_hidden);
      }
      const { voteM, bookmarkM, reportM } = extractMaps(rows);
      if (reset) {
        setPosts(rows); setVoteMap(voteM); setBookmarkMap(bookmarkM); setReportMap(reportM); setPage(1);
      } else {
        setPosts(prev => [...prev, ...rows]);
        setVoteMap(prev => ({ ...prev, ...voteM }));
        setBookmarkMap(prev => ({ ...prev, ...bookmarkM }));
        setReportMap(prev => ({ ...prev, ...reportM }));
        setPage(p => p + 1);
      }
      setHasMore(rows.length === POSTS_PER_PAGE);
    } catch (err) {
      console.error("fetchPosts error:", err);
    } finally {
      setLoadingPosts(false);
    }
  }, [feedUser, feedTab, sortBy, activeTag, searchQuery, page]);

  useEffect(() => {
    if (!feedUser) return;
    setPage(0); setPosts([]);
    fetchPosts(true);
  }, [feedUser, feedTab, sortBy, activeTag, searchQuery]);

  useEffect(() => {
    supabase.rpc("get_trending_tags", { p_limit: 5 }).then(({ data }) => {
      if (data) setTrendingTags(data.map(r => [r.tag, r.count]));
    });
  }, []);

  const handleSpaceSearch = useCallback(async (q) => {
    if (!feedUser) return;
    setSearchingSpaces(true);
    try {
      const results = await searchSpaces(q || null, feedUser.id, 6);
      setSpaceResults(results);
    } catch (_) {}
    finally { setSearchingSpaces(false); }
  }, [feedUser]);

  useEffect(() => {
    if (feedUser) handleSpaceSearch(spaceSearch);
  }, [spaceSearch, feedUser]);

  const handleUsernameChange = useCallback(async () => {
    if (!newUsername.trim()) return;
    setUsernameLoading(true); setUsernameError(null);
    try {
      const result = await changeUsername(newUsername.trim());
      if (result.success) {
        updateFeedUser({ username: result.username, username_changed_at: new Date().toISOString() });
        setShowUsernameChange(false); setNewUsername("");
      } else { setUsernameError(result.reason); }
    } catch (_) { setUsernameError("Failed to change username. Please try again."); }
    finally { setUsernameLoading(false); }
  }, [newUsername, updateFeedUser]);

  const handlePostCreated  = useCallback((post) => setPosts(prev => [post, ...prev]), []);
  const handlePostDeleted  = useCallback((id)   => setPosts(prev => prev.filter(p => p.id !== id)), []);
  const handleSearch       = useCallback((e)    => { e.preventDefault(); setSearchQuery(searchInput.trim()); }, [searchInput]);
  const handleSpaceCreated = useCallback((space) => { setShowCreateSpace(false); setOpenedSpace(space); }, []);

  const daysUntilUsernameChange = useCallback(() => {
    if (!feedUser?.username_changed_at) return 0;
    const days = Math.ceil((new Date(feedUser.username_changed_at).getTime() + 30 * 86400000 - Date.now()) / 86400000);
    return Math.max(0, days);
  }, [feedUser]);

  const postsWithCards = getPostsWithSpaceCards();

  if (userLoading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "100vh" }}>
        <div style={{ textAlign: "center", color: "var(--text-muted)" }}>
          <div style={{ fontSize: "2rem", marginBottom: 8 }}>💖</div>
          <p>Loading your feed…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container" style={{ maxWidth: 1100, padding: isMobile ? "0 0 80px" : undefined }}>

      {/* ── DESKTOP NAVBAR ── */}
      <nav className="navbar cf-navbar">
        <div className="navbar-left">
          <a href="/dashboard">Home</a>
          <a href="/resources">Resources</a>
          <a href="/community">Community</a>
          <a href="/feed" style={{
            color: "var(--primary-color)", background: "var(--primary-light)",
            borderRadius: "var(--radius-sm)", padding: "7px 12px",
            fontWeight: 600, textDecoration: "none", fontSize: "0.875rem",
          }}>Feed</a>
          <a href="/chatbot">Chat with Saksham</a>
          <div className="nav-dropdown"
            onMouseEnter={() => setShowInsightMenu(true)}
            onMouseLeave={() => setShowInsightMenu(false)}
          >
            <span className="nav-item">Insight ▾</span>
            {showInsightMenu && (
              <div className="dropdown-menu">
                <Link to="/insight" className="dropdown-link">See Saved Diary</Link>
                <Link to="/insight" className="dropdown-link">Look at Report</Link>
              </div>
            )}
          </div>
          <Link to="/chattr" className="nav-item">Chattr</Link>
        </div>
        <div className="navbar-center">
          <span className="healhub-brand"><span className="hug-icon">💖🌿</span> HealHub</span>
          <Link to="/about" className="nav-item">About Us</Link>
        </div>
        <div className="navbar-right">
          {feedUser && <NotificationBell currentUser={feedUser} />}
          {feedUser?.is_admin && (
            <button onClick={() => setShowAdmin(true)} style={{
              background: "var(--primary-light)", border: "1px solid var(--primary-color)",
              color: "var(--primary-color)", borderRadius: "var(--radius-sm)",
              padding: "5px 10px", cursor: "pointer", fontSize: "0.8rem",
              fontWeight: 600, fontFamily: "DM Sans, sans-serif",
            }}>🛡️ Admin</button>
          )}
          <button onClick={toggleTheme} className="theme-switcher-btn">
            {theme === "light-theme" ? <MdOutlineLightMode /> : theme === "dark-theme" ? <MdModeNight /> : <MdFilterVintage />}
          </button>
          <Link to="/profile" className="profile-icon-link"><FaUserCircle size={24} /></Link>
          <button onClick={handleLogout} className="logout-btn">Log Out</button>
        </div>
      </nav>

      {/* ── MOBILE TOP BAR ── */}
      {isMobile && (
        <div className="cf-mobile-only" style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          padding: "10px 14px 8px",
          paddingTop: "calc(env(safe-area-inset-top,0px) + 10px)",
          background: "rgba(252,250,248,.94)",
          backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)",
          borderBottom: "0.5px solid rgba(212,207,202,.6)",
          position: "sticky", top: 0, zIndex: 30,
        }}>
          <span style={{
            fontFamily: "var(--font-display, serif)",
            fontSize: "1.1rem", fontWeight: 700,
            color: "var(--text-color)",
          }}>💖 Community</span>

          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            {/* Theme toggle */}
            <button onClick={toggleTheme} style={{
              width: 32, height: 32, borderRadius: "50%",
              background: "rgba(118,118,128,.1)", border: "none",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: ".9rem", cursor: "pointer", color: "var(--text-color)",
            }}>
              {theme === "light-theme" ? "☀️" : theme === "dark-theme" ? "🌙" : "🌿"}
            </button>

            {/* Notification bell */}
            {feedUser && <NotificationBell currentUser={feedUser} />}

            {/* Admin */}
            {feedUser?.is_admin && (
              <button onClick={() => setShowAdmin(true)} style={{
                width: 32, height: 32, borderRadius: "50%",
                background: "var(--primary-light)", border: "1px solid var(--primary-color)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: ".8rem", cursor: "pointer",
              }}>🛡️</button>
            )}

            {/* More (opens sidebar sheet) */}
            <button
              onClick={() => setShowMobileMore(true)}
              style={{
                width: 32, height: 32, borderRadius: "50%",
                background: "rgba(118,118,128,.1)", border: "none",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "1rem", cursor: "pointer", color: "var(--text-color)",
              }}
            >⋯</button>
          </div>
        </div>
      )}

      {/* ── MODALS ── */}
      {needsUsername && (
        <UsernameSetup
          suggestedName={feedUser?.username}
          onRegister={async (username, interests, bio) => {
            const result = await registerUsername(username, interests);
            if (!result.error && bio) {
              await supabase.from("feed_users").update({ bio, interests }).eq("id", result.data?.id);
            }
            return result;
          }}
        />
      )}
      {showAdmin      && <AdminPanel currentUser={feedUser} onClose={() => setShowAdmin(false)} />}
      {viewingProfile && <UserProfilePanel username={viewingProfile} currentUser={feedUser} onClose={() => setViewingProfile(null)} />}
      {openedSpace && (
        <ErrorBoundary>
          <SpaceFeed spaceId={openedSpace.id} currentUser={feedUser} onClose={() => setOpenedSpace(null)} />
        </ErrorBoundary>
      )}
      {showCreateSpace && <CreateSpaceModal currentUser={feedUser} onCreated={handleSpaceCreated} onClose={() => setShowCreateSpace(false)} />}

      {/* Mobile "More" bottom sheet */}
      {isMobile && showMobileMore && (
        <MobileMoreSheet
          feedUser={feedUser}
          trendingTags={trendingTags}
          topSpaces={topSpaces}
          activeTag={activeTag}
          setActiveTag={setActiveTag}
          onClose={() => setShowMobileMore(false)}
          onOpenSpace={(sp) => { setOpenedSpace(sp); setShowMobileMore(false); }}
          onCreateSpace={() => setShowCreateSpace(true)}
          canCreateSpace={canCreateSpace}
          spaceSearch={spaceSearch}
          setSpaceSearch={setSpaceSearch}
          spaceResults={spaceResults}
          searchingSpaces={searchingSpaces}
        />
      )}

      {/* ── DESKTOP page header ── */}
      {!isMobile && (
        <div style={{ marginBottom: 24 }}>
          <h2 style={{ margin: 0, marginBottom: 4 }}>Community Feed 💬</h2>
          <p style={{ margin: 0, fontSize: "0.9rem", color: "var(--text-muted)" }}>
            A safe space to share, connect, and grow together.
            {feedUser && !needsUsername && (
              <span style={{ marginLeft: 8, color: "var(--primary-color)", fontWeight: 600 }}>
                Posting as @{feedUser.username}
              </span>
            )}
          </p>
        </div>
      )}

      {/* ── MAIN LAYOUT ── */}
      <div style={{
        display: "grid",
        gridTemplateColumns: isMobile ? "1fr" : "1fr 280px",
        gap: isMobile ? 0 : 24,
        alignItems: "start",
      }}>

        {/* ══ LEFT / MAIN FEED ══ */}
        <div style={{ padding: isMobile ? "0 12px" : 0 }}>

          {/* Mobile: user mini stats */}
          {isMobile && feedUser && !needsUsername && (
            <div style={{
              display: "flex", alignItems: "center", gap: 12,
              padding: "12px 4px 8px",
              borderBottom: "1px solid var(--border-color)",
              marginBottom: 12,
            }}>
              {/* Avatar */}
              <div style={{
                width: 38, height: 38, borderRadius: "50%",
                background: "var(--primary-light)", color: "var(--primary-color)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontWeight: 700, fontSize: ".9rem",
                border: "2px solid var(--primary-color)", flexShrink: 0,
              }}>{feedUser.username?.[0]?.toUpperCase()}</div>

              {/* Name */}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 700, fontSize: ".85rem", color: "var(--text-color)" }}>
                  @{feedUser.username}
                </div>
                {feedUser.bio && (
                  <div style={{
                    fontSize: ".7rem", color: "var(--text-muted)",
                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                  }}>{feedUser.bio}</div>
                )}
              </div>

              {/* Stats */}
              {[
                { label: "Posts", value: feedUser.post_count ?? 0 },
                { label: "Followers", value: feedUser.follower_count ?? 0 },
                { label: "Following", value: feedUser.following_count ?? 0 },
              ].map((s, i) => (
                <div key={s.label} style={{
                  textAlign: "center", flexShrink: 0,
                  borderLeft: "1px solid var(--border-color)", paddingLeft: 10,
                }}>
                  <div style={{
                    fontFamily: "var(--font-display, serif)",
                    fontSize: ".95rem", fontWeight: 700, color: "var(--text-color)",
                  }}>{s.value}</div>
                  <div style={{
                    fontSize: ".56rem", fontWeight: 700,
                    color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: ".05em",
                  }}>{s.label}</div>
                </div>
              ))}
            </div>
          )}

          {/* Composer */}
          {feedUser && !needsUsername && !feedUser.is_banned && (
            <PostComposer feedUser={feedUser} onPostCreated={handlePostCreated} />
          )}
          {feedUser?.is_banned && (
            <div className="card" style={{
              marginBottom: 20, borderColor: "#fca5a5",
              background: "#fff1f0", padding: "16px 20px",
            }}>
              <p style={{ color: "#dc2626", fontWeight: 600, margin: 0 }}>
                🚫 Your account has been suspended from posting.
              </p>
            </div>
          )}

          {/* Feed tabs + sort */}
          <div style={{
            display: "flex", gap: 6, marginBottom: isMobile ? 10 : 16,
            flexWrap: "wrap", alignItems: "center",
            justifyContent: "space-between",
          }}>
            <div style={{ display: "flex", gap: 5, overflowX: "auto", scrollbarWidth: "none", WebkitOverflowScrolling: "touch" }}>
              {[
                { key: "all",       label: "🌍 All" },
                { key: "following", label: "👥 Following" },
                { key: "mine",      label: "📝 Mine" },
                { key: "bookmarks", label: "🔖 Saved" },
              ].map(tab => (
                <button
                  key={tab.key}
                  onClick={() => setFeedTab(tab.key)}
                  style={{
                    padding: isMobile ? "7px 13px" : "7px 14px",
                    borderRadius: "var(--radius-full)",
                    border:     feedTab === tab.key ? "1.5px solid var(--primary-color)" : "1.5px solid var(--border-color)",
                    background: feedTab === tab.key ? "var(--primary-light)" : "var(--card-bg-color)",
                    color:      feedTab === tab.key ? "var(--primary-color)" : "var(--text-secondary-color)",
                    fontSize: isMobile ? ".76rem" : "0.8rem", fontWeight: 600, cursor: "pointer",
                    fontFamily: "DM Sans, sans-serif", flexShrink: 0, whiteSpace: "nowrap",
                  }}
                >{tab.label}</button>
              ))}
            </div>

            {/* Sort chips */}
            <div style={{ display: "flex", gap: 4 }}>
              {SORT_OPTIONS.map(opt => (
                <button
                  key={opt.value}
                  onClick={() => setSortBy(opt.value)}
                  style={{
                    padding: isMobile ? "5px 10px" : "5px 12px",
                    borderRadius: "var(--radius-full)",
                    border:     sortBy === opt.value ? "1.5px solid var(--secondary-color)" : "1.5px solid var(--border-color)",
                    background: sortBy === opt.value ? "var(--secondary-light)" : "transparent",
                    color:      sortBy === opt.value ? "var(--secondary-color)" : "var(--text-muted)",
                    fontSize: ".76rem", fontWeight: 600, cursor: "pointer",
                    fontFamily: "DM Sans, sans-serif",
                  }}
                >{opt.label}</button>
              ))}
            </div>
          </div>

          {/* Mobile trending tag strip */}
          {isMobile && trendingTags.length > 0 && (
            <div style={{
              display: "flex", gap: 7, overflowX: "auto",
              scrollbarWidth: "none", WebkitOverflowScrolling: "touch",
              paddingBottom: 4, marginBottom: 10,
            }}>
              <button
                onClick={() => setActiveTag(null)}
                style={{
                  flexShrink: 0, padding: "5px 13px", borderRadius: 99,
                  border: !activeTag ? "1.5px solid var(--text-color)" : "1.5px solid var(--border-color)",
                  background: !activeTag ? "var(--bg-secondary)" : "transparent",
                  color: !activeTag ? "var(--text-color)" : "var(--text-muted)",
                  fontSize: ".73rem", fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                  whiteSpace: "nowrap",
                }}
              >All</button>
              {TAGS.map(t => (
                <button
                  key={t.label}
                  onClick={() => setActiveTag(activeTag === t.label ? null : t.label)}
                  style={{
                    flexShrink: 0, padding: "5px 13px", borderRadius: 99,
                    border: activeTag === t.label ? `1.5px solid ${t.color}` : "1.5px solid var(--border-color)",
                    background: activeTag === t.label ? `${t.color}18` : "transparent",
                    color: activeTag === t.label ? t.color : "var(--text-muted)",
                    fontSize: ".73rem", fontWeight: 700, cursor: "pointer",
                    fontFamily: "inherit", whiteSpace: "nowrap",
                  }}
                >{t.emoji} {t.label}</button>
              ))}
            </div>
          )}

          {/* Desktop tag strip */}
          {!isMobile && (
            <div style={{ display: "flex", gap: 8, marginBottom: 16, overflowX: "auto", paddingBottom: 4 }}>
              <button onClick={() => setActiveTag(null)} style={{
                padding: "4px 14px", borderRadius: "var(--radius-full)", flexShrink: 0,
                border: !activeTag ? "1.5px solid var(--text-muted)" : "1.5px solid var(--border-color)",
                background: !activeTag ? "var(--bg-secondary)" : "transparent",
                color: !activeTag ? "var(--text-color)" : "var(--text-muted)",
                fontSize: "0.78rem", fontWeight: 600, cursor: "pointer", fontFamily: "DM Sans, sans-serif",
              }}>All</button>
              {TAGS.map(t => (
                <button key={t.label} onClick={() => setActiveTag(activeTag === t.label ? null : t.label)} style={{
                  padding: "4px 14px", borderRadius: "var(--radius-full)", flexShrink: 0,
                  border: activeTag === t.label ? `1.5px solid ${t.color}` : "1.5px solid var(--border-color)",
                  background: activeTag === t.label ? `${t.color}18` : "transparent",
                  color: activeTag === t.label ? t.color : "var(--text-muted)",
                  fontSize: "0.78rem", fontWeight: 600, cursor: "pointer",
                  fontFamily: "DM Sans, sans-serif",
                }}>{t.emoji} {t.label}</button>
              ))}
            </div>
          )}

          {/* Search */}
          <form onSubmit={handleSearch} style={{ marginBottom: 16, display: "flex", gap: 8 }}>
            {isMobile ? (
              <div style={{
                flex: 1, display: "flex", alignItems: "center", gap: 8,
                background: "rgba(118,118,128,.1)", borderRadius: 13, padding: "9px 12px",
              }}>
                <span style={{ fontSize: 14, opacity: .5 }}>🔍</span>
                <input
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  placeholder="Search posts…"
                  style={{
                    flex: 1, border: "none", background: "transparent", padding: 0,
                    color: "var(--text-color)", fontFamily: "inherit",
                    fontSize: ".88rem", fontWeight: 500, outline: "none",
                  }}
                />
                {searchQuery && (
                  <button
                    type="button"
                    onClick={() => { setSearchQuery(""); setSearchInput(""); }}
                    style={{
                      border: "none", background: "rgba(118,118,128,.28)", borderRadius: "50%",
                      width: 18, height: 18, display: "flex", alignItems: "center", justifyContent: "center",
                      fontSize: 9, color: "#fff", fontWeight: 900, cursor: "pointer", padding: 0, flexShrink: 0,
                    }}
                  >✕</button>
                )}
              </div>
            ) : (
              <>
                <input
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  placeholder="🔍 Search posts…"
                  style={{ flex: 1, background: "var(--input-bg)", color: "var(--text-color)" }}
                />
                {searchQuery && (
                  <button type="button" onClick={() => { setSearchQuery(""); setSearchInput(""); }} style={{
                    padding: "8px 14px", borderRadius: "var(--radius-md)",
                    border: "1px solid var(--border-color)", background: "var(--bg-secondary)",
                    color: "var(--text-muted)", cursor: "pointer", fontFamily: "DM Sans, sans-serif", fontSize: "0.85rem",
                  }}>Clear</button>
                )}
              </>
            )}
          </form>

          {/* Posts */}
          {loadingPosts && posts.length === 0 ? (
            <div style={{ textAlign: "center", padding: "48px 0", color: "var(--text-muted)" }}>
              <div style={{ fontSize: "2rem", marginBottom: 8 }}>🌱</div>
              <p>Loading posts…</p>
            </div>
          ) : posts.length === 0 ? (
            <div className="card" style={{ textAlign: "center", padding: "48px 32px" }}>
              <div style={{ fontSize: "2.5rem", marginBottom: 12 }}>
                {feedTab === "following" ? "👥" : feedTab === "bookmarks" ? "🔖" : "✨"}
              </div>
              <h3 style={{ marginBottom: 8 }}>
                {feedTab === "following"  ? "Follow people to see their posts here"
               : feedTab === "bookmarks" ? "No saved posts yet"
               : searchQuery             ? "No posts match your search"
               :                           "Be the first to post!"}
              </h3>
              <p style={{ margin: 0, fontSize: "0.9rem", color: "var(--text-muted)" }}>
                {feedTab === "following"
                  ? "Discover and follow users by clicking their username on any post →"
                  : "Share a thought, ask a question, or start a discussion."}
              </p>
            </div>
          ) : (
            <>
              <ErrorBoundary>
                {postsWithCards.map(item =>
                  item.__isSpaceCard ? (
                    <SpaceCard
                      key={`space-card-${item.space.id}`}
                      space={item.space}
                      currentUser={feedUser}
                      onOpenSpace={setOpenedSpace}
                    />
                  ) : (
                    <PostCard
                      key={item.id}
                      post={item}
                      currentUser={feedUser}
                      onDelete={handlePostDeleted}
                      onNavigateToProfile={setViewingProfile}
                      stateProvided={true}
                      initialVote={voteMap[item.id] ?? null}
                      initialBookmarked={bookmarkMap[item.id] ?? false}
                      initialReported={reportMap[item.id] ?? false}
                    />
                  )
                )}
              </ErrorBoundary>
              {hasMore && (
                <div style={{ textAlign: "center", marginTop: 16 }}>
                  <button
                    onClick={() => fetchPosts()}
                    disabled={loadingPosts}
                    className="btn secondary-btn"
                    style={{ padding: "10px 32px", opacity: loadingPosts ? 0.6 : 1 }}
                  >
                    {loadingPosts ? "Loading…" : "Load more posts"}
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* ══ DESKTOP RIGHT SIDEBAR ══ */}
        <div className="cf-desktop-sidebar" style={{
          display: "flex", flexDirection: "column", gap: 16,
          position: "sticky", top: 90,
        }}>

          {/* User card */}
          {feedUser && !needsUsername && (
            <div className="card" style={{ padding: "20px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
                <div style={{
                  width: 44, height: 44, borderRadius: "50%",
                  background: "var(--primary-light)", color: "var(--primary-color)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  fontWeight: 700, fontSize: "1rem", border: "2px solid var(--primary-color)",
                }}>{feedUser.username?.[0]?.toUpperCase()}</div>
                <div style={{ flex: 1 }}>
                  <p style={{ margin: 0, fontWeight: 700, fontSize: "0.9rem", color: "var(--text-color)" }}>@{feedUser.username}</p>
                  {feedUser.bio && <p style={{ margin: 0, fontSize: "0.78rem", color: "var(--text-muted)" }}>{feedUser.bio.slice(0, 40)}</p>}
                </div>
                <button onClick={() => setShowUsernameChange(v => !v)} title="Change username" style={{
                  background: "none", border: "1px solid var(--border-color)",
                  borderRadius: 8, cursor: "pointer", fontSize: "0.72rem",
                  color: "var(--text-muted)", padding: "3px 7px", fontFamily: "inherit",
                }}>✏️</button>
              </div>

              {showUsernameChange && (
                <div style={{ marginBottom: 14, padding: "12px", borderRadius: 10, background: "var(--bg-secondary)", border: "1px solid var(--border-color)" }}>
                  {daysUntilUsernameChange() > 0 ? (
                    <p style={{ fontSize: "0.78rem", color: "var(--text-muted)", margin: 0 }}>
                      🕐 Change again in <strong>{daysUntilUsernameChange()} days</strong>
                    </p>
                  ) : (
                    <>
                      <p style={{ fontSize: "0.75rem", color: "var(--text-muted)", margin: "0 0 8px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>Change Username</p>
                      <input value={newUsername} onChange={e => setNewUsername(e.target.value)} placeholder="new_username" style={{
                        width: "100%", padding: "7px 10px", borderRadius: 7,
                        border: "1.5px solid var(--border-color)", background: "var(--card-bg-color)",
                        color: "var(--text-color)", fontSize: "0.82rem",
                        fontFamily: "inherit", outline: "none", boxSizing: "border-box", marginBottom: 8,
                      }} />
                      {usernameError && <p style={{ fontSize: "0.75rem", color: "#ef4444", margin: "0 0 8px" }}>{usernameError}</p>}
                      <div style={{ display: "flex", gap: 6 }}>
                        <button onClick={handleUsernameChange} disabled={usernameLoading || !newUsername.trim()} style={{
                          flex: 1, padding: "6px 0", borderRadius: 7, border: "none",
                          background: "var(--primary-color)", color: "#fff",
                          fontSize: "0.78rem", fontWeight: 600, cursor: "pointer",
                          opacity: (usernameLoading || !newUsername.trim()) ? 0.6 : 1, fontFamily: "inherit",
                        }}>{usernameLoading ? "Saving…" : "Save"}</button>
                        <button onClick={() => { setShowUsernameChange(false); setNewUsername(""); setUsernameError(null); }} style={{
                          padding: "6px 12px", borderRadius: 7, border: "1px solid var(--border-color)",
                          background: "transparent", color: "var(--text-muted)", fontSize: "0.78rem", cursor: "pointer", fontFamily: "inherit",
                        }}>Cancel</button>
                      </div>
                    </>
                  )}
                </div>
              )}

              <div style={{
                display: "grid", gridTemplateColumns: "1fr 1fr 1fr", textAlign: "center",
                background: "var(--bg-secondary)", borderRadius: "var(--radius-md)",
                padding: "10px 0", border: "1px solid var(--border-color)",
              }}>
                {[
                  { label: "Posts",     value: feedUser.post_count     || 0 },
                  { label: "Followers", value: feedUser.follower_count  || 0 },
                  { label: "Following", value: feedUser.following_count || 0 },
                ].map((s, i) => (
                  <div key={s.label} style={{ borderRight: i < 2 ? "1px solid var(--border-color)" : "none" }}>
                    <div style={{ fontFamily: "DM Serif Display, serif", fontSize: "1.1rem", color: "var(--text-color)" }}>{s.value}</div>
                    <div style={{ fontSize: "0.68rem", color: "var(--text-muted)", fontWeight: 600 }}>{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Spaces sidebar */}
          <div className="card" style={{ padding: "18px 20px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <h4 style={{ margin: 0, fontSize: "0.85rem", textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text-muted)" }}>🏡 Spaces</h4>
              {canCreateSpace && (
                <button onClick={() => setShowCreateSpace(true)} style={{
                  padding: "4px 10px", borderRadius: 8, border: "1.5px solid var(--primary-color)",
                  background: "var(--primary-light)", color: "var(--primary-color)",
                  fontSize: "0.72rem", fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
                }}>+ Create</button>
              )}
            </div>
            <input
              value={spaceSearchInput}
              onChange={e => { setSpaceSearchInput(e.target.value); setSpaceSearch(e.target.value.trim()); }}
              placeholder="🔍 Search spaces…"
              style={{
                width: "100%", padding: "7px 10px", borderRadius: 8,
                border: "1.5px solid var(--border-color)", background: "var(--bg-secondary)",
                color: "var(--text-color)", fontSize: "0.8rem", fontFamily: "inherit",
                outline: "none", boxSizing: "border-box", marginBottom: 10,
              }}
            />
            {searchingSpaces ? (
              <div style={{ textAlign: "center", color: "var(--text-muted)", padding: "12px 0", fontSize: "0.82rem" }}>Searching…</div>
            ) : spaceResults.length === 0 ? (
              <div style={{ textAlign: "center", color: "var(--text-muted)", fontSize: "0.8rem", padding: "8px 0" }}>
                {spaceSearch ? "No spaces found" : "No spaces yet"}
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {spaceResults.map(space => (
                  <button key={space.id} onClick={() => setOpenedSpace(space)} style={{
                    display: "flex", alignItems: "center", gap: 10, padding: "8px 10px",
                    borderRadius: 10, border: "1.5px solid var(--border-color)",
                    background: "var(--bg-secondary)", cursor: "pointer",
                    textAlign: "left", fontFamily: "inherit",
                  }}>
                    <span style={{ fontSize: 20, flexShrink: 0 }}>{space.avatar_emoji || "🏡"}</span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: "0.82rem", fontWeight: 700, color: "var(--text-color)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{space.name}</div>
                      <div style={{ fontSize: "0.7rem", color: "var(--text-muted)" }}>{space.member_count} members</div>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Trending */}
          {trendingTags.length > 0 && (
            <div className="card" style={{ padding: "18px 20px" }}>
              <h4 style={{ marginBottom: 12, fontSize: "0.85rem", textTransform: "uppercase", letterSpacing: "0.05em", color: "var(--text-muted)" }}>🔥 Trending This Week</h4>
              {trendingTags.map(([tag, count]) => {
                const tagInfo = TAGS.find(t => t.label === tag) || {};
                return (
                  <button key={tag} onClick={() => setActiveTag(tag === activeTag ? null : tag)} style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    width: "100%", padding: "8px 0", background: "none", border: "none",
                    borderBottom: "1px solid var(--border-subtle, #f3f4f6)", cursor: "pointer",
                    color: "var(--text-color)", fontFamily: "DM Sans, sans-serif",
                  }}>
                    <span style={{ fontSize: "0.875rem", fontWeight: 600 }}>{tagInfo.emoji} {tag}</span>
                    <span style={{ fontSize: "0.78rem", color: "var(--text-muted)" }}>{count} posts</span>
                  </button>
                );
              })}
            </div>
          )}

          {/* Guidelines */}
          <div className="card" style={{ padding: "16px 20px", background: "linear-gradient(135deg, var(--secondary-light), var(--card-bg-color))" }}>
            <h4 style={{ marginBottom: 10, fontSize: "0.85rem" }}>💚 Community Guidelines</h4>
            <p style={{ fontSize: "0.78rem", margin: 0, lineHeight: 1.7, color: "var(--text-muted)" }}>
              Be kind and supportive. No hate, harassment, or explicit content.
              Use the report button for violations.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ─── Root export ─────────────────────────────────────────────────────────── */
export default function CommunityFeed() {
  const { feedUser, loading: userLoading, needsUsername, registerUsername, updateFeedUser } = useFeedUser();
  return (
    <FeedProvider currentUser={feedUser}>
      <CommunityFeedInner
        feedUser={feedUser}
        userLoading={userLoading}
        needsUsername={needsUsername}
        registerUsername={registerUsername}
        updateFeedUser={updateFeedUser}
      />
    </FeedProvider>
  );
}